import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import classNames from 'classnames';
import scheduleService from '../../../../services/community/scheduleService';
import communityService from '../../../../services/community/communityService';
import { ScheduleCommunityModel, ScheduleModel } from '../../../../models/schedule.model';
import { RootState } from '../../../../redux/reducers';
import { setState } from '../../../../redux/slices/applicationSlice';
import { store } from '../../../../redux/store';
import useSelectedBusiness from '../../../../hooks/business/useSelectedBusiness';
import Community, { CommunityFeature } from '../../../../types/community';
import Button from '../../../components/buttons/Button';
import SearchBar from '../../../components/forms/SearchBar';
import ProfilePicture from '../../../components/misc/ProfilePicture';
import EmptyScreenView from '../../../components/misc/EmptyScreenView';

type ScheduleCommunitiesTabProps = {
  schedule: ScheduleModel;
  onCommunitiesUpdated: () => Promise<void>;
};

export default function ScheduleCommunitiesTab({
  schedule,
  onCommunitiesUpdated,
}: ScheduleCommunitiesTabProps): JSX.Element {
  const { t } = useTranslation();
  const selectedBusiness = useSelectedBusiness();
  const selectedBusinessId = useSelector((state: RootState) => state.application.selectedBusiness);

  const [linkedCommunities, setLinkedCommunities] = useState<ScheduleCommunityModel[]>([]);
  const [availableCommunities, setAvailableCommunities] = useState<Community[]>([]);
  const [selectedCommunityIds, setSelectedCommunityIds] = useState<number[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const mainCommunityId = selectedBusiness?.community?.id;

  const loadData = useCallback(async () => {
    if (!selectedBusinessId) return;

    try {
      setLoading(true);
      const [linked, businessCommunities] = await Promise.all([
        scheduleService.listScheduleCommunities(schedule.scheduleId),
        communityService.fetchCommunitiesForBusiness(selectedBusinessId),
      ]);

      setLinkedCommunities(linked);
      setSelectedCommunityIds(linked.map((community) => community.communityId));
      setAvailableCommunities(
        businessCommunities.filter((community) => community.id !== mainCommunityId),
      );
    } catch {
      toast.error(t('general.error'));
    } finally {
      setLoading(false);
    }
  }, [mainCommunityId, schedule.scheduleId, selectedBusinessId, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredCommunities = useMemo(() => {
    const query = search.trim().toLowerCase();

    return availableCommunities
      .filter(
        (community) =>
          !query ||
          community.name.toLowerCase().includes(query) ||
          community.type.toLowerCase().includes(query),
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [availableCommunities, search]);

  const toggleCommunity = (communityId: number) => {
    setSelectedCommunityIds((previous) => {
      if (previous.includes(communityId)) {
        return previous.filter((id) => id !== communityId);
      }

      return [...previous, communityId];
    });
  };

  const selectAll = () => {
    setSelectedCommunityIds(availableCommunities.map((community) => community.id));
  };

  const clearAll = () => {
    setSelectedCommunityIds([]);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const previouslyLinkedIds = new Set(linkedCommunities.map((community) => community.communityId));
      const newlyLinkedIds = selectedCommunityIds.filter((id) => !previouslyLinkedIds.has(id));

      const updated = await scheduleService.setScheduleCommunities(schedule.scheduleId, {
        communityIds: selectedCommunityIds,
      });
      setLinkedCommunities(updated);

      if (newlyLinkedIds.length > 0) {
        const { application } = store.getState();
        store.dispatch(
          setState({
            ...application,
            communities: application.communities?.map((community: Community) =>
              newlyLinkedIds.includes(community.id)
                ? {
                    ...community,
                    features: {
                      ...community.features,
                      [CommunityFeature.SCHEDULES]: true,
                    },
                  }
                : community,
            ),
          }),
        );
      }

      await onCommunitiesUpdated();
      toast.success(t('page.event.schedules.communities.messages.saved'));
    } catch {
      toast.error(t('general.error'));
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = useMemo(() => {
    const current = new Set(linkedCommunities.map((community) => community.communityId));
    const selected = new Set(selectedCommunityIds);

    if (current.size !== selected.size) {
      return true;
    }

    return selectedCommunityIds.some((id) => !current.has(id));
  }, [linkedCommunities, selectedCommunityIds]);

  if (loading) {
    return <div className="py-8 text-sm text-primary-500">{t('general.loading')}</div>;
  }

  if (availableCommunities.length === 0) {
    return (
      <EmptyScreenView
        subtitle={t('page.event.schedules.communities.emptySubCommunities')}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h3 className="text-lg font-semibold text-primary-900">
          {t('page.event.schedules.communities.title')}
        </h3>
        <p className="mt-1 text-sm text-primary-500">
          {t('page.event.schedules.communities.subtitle')}
        </p>
      </div>

      <SearchBar search={search} setSearch={setSearch} />

      <div className="flex items-center gap-2">
        <Button variant="secondary" onClick={selectAll}>
          {t('page.event.schedules.fastAssigning.selectAll')}
        </Button>
        <Button variant="secondary" onClick={clearAll}>
          {t('page.event.schedules.fastAssigning.clear')}
        </Button>
      </div>

      <div className="flex max-h-[420px] flex-col gap-2 overflow-y-auto border-y border-primary-200 py-4">
        {filteredCommunities.length === 0 ? (
          <p className="py-4 text-sm text-gray-500">{t('page.event.schedules.communities.emptySearch')}</p>
        ) : (
          filteredCommunities.map((community) => {
            const isSelected = selectedCommunityIds.includes(community.id);

            return (
              <button
                key={community.id}
                type="button"
                onClick={() => toggleCommunity(community.id)}
                className={classNames(
                  'flex w-full items-center justify-between rounded-[8px] border border-secondary-200 px-3 py-2 text-left transition-all',
                  {
                    'bg-secondary-200': isSelected,
                  },
                )}>
                <div className="flex min-w-0 items-center gap-2">
                  <ProfilePicture
                    alias={community.name}
                    picture={community.picture ?? undefined}
                    className="h-9 w-9"
                  />
                  <div className="min-w-0">
                    <p className="truncate font-medium">{community.name}</p>
                    <p className="truncate text-[13px] text-gray-500">{community.type}</p>
                  </div>
                </div>
                <input type="checkbox" checked={isSelected} readOnly />
              </button>
            );
          })
        )}
      </div>

      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-primary-700">
          {t('page.event.schedules.communities.selected', { count: selectedCommunityIds.length })}
        </p>
        <Button
          variant="primary"
          onClick={handleSave}
          loading={saving}
          disabled={!hasChanges || saving}>
          {t('general.save')}
        </Button>
      </div>
    </div>
  );
}
